<div class="container-fluid complyon-page training-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell training-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title training-title mb-2 text-l">Training program</h1>
      <p class="complyon-subtitle training-subtitle mb-0 text-s">
        Ongoing training - annual refreshers, recertification, awareness campaigns. Pre-activation training is run by OnboardOS.
      </p>
    </section>

    <section class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div class="d-flex flex-wrap align-items-center gap-1">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn fw-semibold"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== undefined" class="doa-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <button type="button" class="btn doa-reset-btn d-inline-flex align-items-center gap-1">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </section>

    <div v-if="active_tab === 'matrix'">
      <section class="mb-3">
        <div class="rounded-4 border px-3 py-3" style="background:#e8f8f5;border-color:#bce9df;">
          <div class="d-flex align-items-start gap-2 text-s">
            <i class="bi bi-mortarboard mt-1 text-success"></i>
            <div>
              <strong>Mandatory training matrix.</strong>
              Each course links to the policy it enforces, the role it applies to and the control it supports - proof that obligations are trained, assessed and certified.
            </div>
          </div>
        </div>
      </section>

      <section class="row g-3 mb-3">
        <div v-for="card in matrix_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value text-l" :class="card.accent === 'green' ? 'text-success' : card.accent === 'amber' ? 'text-warning-emphasis' : card.accent === 'red' ? 'text-danger' : 'text-dark'">{{ card.value }}</div>
            <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Training matrix</div>
            <div class="complyon-section-subtitle text-xs">Mandatory courses with audience, linked policy & control, completion and assessment.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_course_modal">
            <i class="bi bi-plus-lg"></i>
            <span>New course</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Course</th>
                <th class="text-xs">Category</th>
                <th class="text-xs">Audience</th>
                <th class="text-xs">Policy</th>
                <th class="text-xs">Control</th>
                <th class="text-xs">Completion</th>
                <th class="text-xs">Pass</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in matrix_rows"
                :key="row.course"
                class="facilities-row-clickable"
                :class="{ 'bg-light': row.course === 'Delegation of authority' }"
                role="button"
                tabindex="0"
                @click="open_course_drawer(row.course)"
                @keydown.enter.prevent="open_course_drawer(row.course)"
                @keydown.space.prevent="open_course_drawer(row.course)"
              >
                <td class="ps-3"><div class="procurement-name">{{ row.course }}</div></td>
                <td><span class="badge complyon-badge text-xs bg-light text-secondary border border-light">{{ row.category }}</span></td>
                <td>{{ row.audience }}</td>
                <td>{{ row.policy }}</td>
                <td class="text-success">{{ row.control }}</td>
                <td>
                  <div class="d-flex align-items-center gap-3">
                    <div class="employees-progress flex-grow-1">
                      <div class="progress rounded-pill">
                        <div class="progress-bar rounded-pill" :style="{ width: `${row.completion}%`, backgroundColor: row.accent }"></div>
                      </div>
                    </div>
                    <div class="employees-progress-value">{{ row.completion }}%</div>
                  </div>
                </td>
                <td class="fw-semibold">{{ row.pass }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'results'">
      <section class="row g-3 mb-3">
        <div v-for="card in results_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value text-l" :class="card.accent === 'green' ? 'text-success' : card.accent === 'amber' ? 'text-warning-emphasis' : card.accent === 'red' ? 'text-danger' : 'text-dark'">{{ card.value }}</div>
            <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Assessment results & certificates</div>
            <div class="complyon-section-subtitle text-xs">Individual training outcomes - score, completion date and certificate number.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_result_modal">
            <i class="bi bi-plus-lg"></i>
            <span>Add record</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Employee</th>
                <th class="text-xs">Dept</th>
                <th class="text-xs">Course</th>
                <th class="text-xs">Result</th>
                <th class="text-xs">Score</th>
                <th class="text-xs">Completed</th>
                <th class="text-xs">Certificate</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in results_rows"
                :key="`${row.employee}-${row.course}-${index}`"
                class="facilities-row-clickable"
                role="button"
                tabindex="0"
                @click="open_result_editor(row, index)"
                @keydown.enter.prevent="open_result_editor(row, index)"
                @keydown.space.prevent="open_result_editor(row, index)"
              >
                <td class="ps-3"><div class="procurement-name">{{ row.employee }}</div></td>
                <td>{{ row.dept }}</td>
                <td>{{ row.course }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.result }}</span></td>
                <td class="fw-semibold">{{ row.score }}</td>
                <td class="fw-semibold">{{ row.completed }}</td>
                <td>
                  <span v-if="row.certificate && row.certificate !== '—' && row.certificate !== 'â€”' && row.certificate !== 'Ã¢â‚¬â€'" class="badge rounded-3 bg-info-subtle text-info-emphasis text-xs">{{ row.certificate }}</span>
                  <span v-else>—</span>
                </td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'refreshers'">
      <section class="mb-3">
        <div class="rounded-4 border px-3 py-3" style="background:#e8f8f5;border-color:#bce9df;">
          <div class="d-flex align-items-start gap-2 text-s">
            <i class="bi bi-arrow-repeat mt-1 text-success"></i>
            <div>
              <strong>Training that closes the loop.</strong>
              When an incident, control failure or audit finding occurs, Complyon auto-assigns a refresher to the affected group - and the resulting certificates become the evidence that closes the corrective action.
            </div>
          </div>
        </div>
      </section>

      <section class="row g-3 mb-3">
        <div v-for="card in refreshers_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value text-l" :class="card.accent === 'green' ? 'text-success' : card.accent === 'amber' ? 'text-warning-emphasis' : card.accent === 'red' ? 'text-danger' : 'text-dark'">{{ card.value }}</div>
            <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Incident-triggered refresher training</div>
            <div class="complyon-section-subtitle text-xs">Each refresher links back to the event that triggered it and forward to the corrective action it evidences.</div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Course</th>
                <th class="text-xs">Department</th>
                <th class="text-xs">Triggered by</th>
                <th class="text-xs">Completion</th>
                <th class="text-xs">Corrective action</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in refresher_rows"
                :key="row.ref"
                class="facilities-row-clickable"
                role="button"
                tabindex="0"
                @click="open_refresher_destination(row)"
                @keydown.enter.prevent="open_refresher_destination(row)"
                @keydown.space.prevent="open_refresher_destination(row)"
              >
                <td class="ps-3"><div class="procurement-id">{{ row.ref }}</div></td>
                <td><div class="procurement-name">{{ row.course }}</div></td>
                <td>{{ row.department }}</td>
                <td>{{ row.triggered_by }}</td>
                <td>
                  <div class="d-flex align-items-center gap-3">
                    <div class="employees-progress flex-grow-1">
                      <div class="progress rounded-pill">
                        <div class="progress-bar rounded-pill" :style="{ width: `${row.completion}%`, backgroundColor: row.accent }"></div>
                      </div>
                    </div>
                    <div class="employees-progress-value">{{ row.completion }}%</div>
                  </div>
                </td>
                <td>
                  <span class="badge rounded-3 bg-info-subtle text-info-emphasis text-xs">{{ row.corrective }}</span>
                </td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'reminders'">
      <section class="row g-3 mb-3">
        <div v-for="card in reminders_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value text-l" :class="card.accent === 'green' ? 'text-success' : card.accent === 'amber' ? 'text-warning-emphasis' : card.accent === 'red' ? 'text-danger' : 'text-dark'">{{ card.value }}</div>
            <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Retraining schedule</div>
            <div class="complyon-section-subtitle text-xs">Courses by cohort expiry - retrain before certification lapses.</div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Course</th>
                <th class="text-xs">Audience</th>
                <th class="text-xs">Expires</th>
                <th class="text-xs">Days</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in reminders_rows"
                :key="`${row.course}-${row.audience}`"
                class="facilities-row-clickable"
                role="button"
                tabindex="0"
                @click="open_course_drawer(row.course)"
                @keydown.enter.prevent="open_course_drawer(row.course)"
                @keydown.space.prevent="open_course_drawer(row.course)"
              >
                <td class="ps-3"><div class="procurement-name">{{ row.course }}</div></td>
                <td>{{ row.audience }}</td>
                <td class="fw-semibold">{{ row.expires }}</td>
                <td :class="row.status.label === 'Expired' ? 'text-danger fw-semibold' : 'fw-semibold'">{{ row.days }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'departments'">
      <section class="row g-3">
        <div v-for="dept in department_cards" :key="dept.dept" class="col-12 col-md-6 col-xl-4">
          <article class="complyon-card rounded-4 p-3 h-100">
            <div class="d-flex align-items-start justify-content-between gap-3">
              <div class="complyon-section-title text-s">{{ dept.dept }}</div>
              <span class="badge rounded-pill" :class="dept.accent === 'green' ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'">{{ dept.value }}</span>
            </div>
            <div class="mt-2">
              <div class="d-flex align-items-center gap-2">
                <div class="complyon-stat-value text-l" :class="dept.accent === 'green' ? 'text-success' : 'text-danger'">{{ dept.certified }}</div>
                <div class="text-xs">certified</div>
              </div>
            </div>
            <div class="mt-3">
              <div class="rounded-1 overflow-hidden" style="height:8px;background:#edf1f6;">
                <div class="h-100 rounded-1" :style="{ width: `${dept.bar}%`, backgroundColor: dept.accent === 'green' ? '#13856f' : '#c1261a' }"></div>
              </div>
            </div>
            <div class="mt-3">
              <div v-for="item in dept.items" :key="`${dept.dept}-${item.name}`" class="d-flex align-items-center justify-content-between gap-3 py-2">
                <div class="d-flex align-items-center gap-2 min-w-0">
                  <span class="rounded-circle flex-shrink-0" :style="{ width: '8px', height: '8px', backgroundColor: item.color }"></span>
                  <div class="text-truncate">{{ item.name }}</div>
                </div>
                <div class="fw-semibold text-xs">{{ item.score }}</div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>

    <div
      v-if="show_result_modal"
      class="finance-modal-backdrop"
      @click.self="close_result_modal"
    >
      <div
        class="finance-modal-shell employees-modal training-result-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="result_modal_mode === 'edit' ? 'Edit assessment record' : 'New assessment record'"
      >
        <div class="finance-modal-header training-result-modal-header">
          <div>
            <h2 class="finance-modal-title">{{ result_modal_mode === 'edit' ? 'Edit assessment record' : 'New assessment record' }}</h2>
            <p class="finance-modal-subtitle">
              An individual&apos;s training result and certificate.
            </p>
          </div>
          <button type="button" class="finance-modal-close" @click="close_result_modal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="finance-modal-body training-result-modal-body">
          <div class="training-result-field training-result-field-full">
            <label class="employees-modal-label">Employee</label>
            <input
              v-model="result_form.employee"
              type="text"
              class="employees-modal-input"
              placeholder="Name"
            />
          </div>

          <div class="training-result-grid">
            <div class="training-result-field">
              <label class="employees-modal-label">Department</label>
              <select v-model="result_form.department" class="employees-modal-input">
                <option v-for="department in training_departments" :key="department" :value="department">{{ department }}</option>
              </select>
            </div>
            <div class="training-result-field">
              <label class="employees-modal-label">Course</label>
              <input
                v-model="result_form.course"
                type="text"
                class="employees-modal-input"
                placeholder="Course name"
              />
            </div>
            <div class="training-result-field">
              <label class="employees-modal-label">Result</label>
              <select v-model="result_form.result" class="employees-modal-input">
                <option v-for="result in training_result_options" :key="result" :value="result">{{ result }}</option>
              </select>
            </div>
            <div class="training-result-field">
              <label class="employees-modal-label">Score (%)</label>
              <input
                v-model="result_form.score"
                type="number"
                min="0"
                max="100"
                class="employees-modal-input"
              />
            </div>
            <div class="training-result-field">
              <label class="employees-modal-label">Completed date</label>
              <input
                v-model="result_form.completed"
                type="text"
                class="employees-modal-input"
                placeholder="e.g. 10 Jan 2026"
              />
            </div>
            <div class="training-result-field">
              <label class="employees-modal-label">Certificate no.</label>
              <input
                v-model="result_form.certificate"
                type="text"
                class="employees-modal-input"
                placeholder="e.g. CERT-8841 (blank if none)"
              />
            </div>
            <div class="training-result-field training-result-field-status">
              <label class="employees-modal-label">Status</label>
              <select v-model="result_form.status" class="employees-modal-input">
                <option v-for="status in training_record_statuses" :key="status" :value="status">{{ status }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="finance-modal-footer employees-modal-footer">
          <button
            v-if="result_modal_mode === 'edit'"
            type="button"
            class="btn btn-link text-decoration-none training-result-delete-btn"
            @click="delete_result_record"
          >
            <i class="bi bi-trash3 me-1"></i>
            <span>Delete</span>
          </button>
          <button type="button" class="btn btn-link text-muted text-decoration-none" @click="close_result_modal">Cancel</button>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="save_result_record">
            <i :class="result_modal_mode === 'edit' ? 'bi bi-check2-square' : 'bi bi-plus-lg'"></i>
            <span>{{ result_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="show_course_modal"
      class="finance-modal-backdrop"
      @click.self="close_course_modal"
    >
      <div
        class="finance-modal-shell employees-modal"
        role="dialog"
        aria-modal="true"
        aria-label="New course"
      >
        <div class="finance-modal-header">
          <div>
            <h2 class="finance-modal-title">New course</h2>
            <p class="finance-modal-subtitle">
              A training course linked to a policy, an audience role and optionally a control.
            </p>
          </div>
          <button type="button" class="finance-modal-close" @click="close_course_modal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="finance-modal-body">
          <div class="command-centre-field">
            <label class="command-centre-label">Course</label>
            <input
              v-model="course_form.course"
              type="text"
              class="form-control command-centre-input"
              placeholder="e.g. Anti-bribery training"
            />
          </div>

          <div class="row g-3">
            <div class="col-12 col-md-6">
              <div class="command-centre-field">
                <label class="command-centre-label">Category</label>
                <select v-model="course_form.category" class="form-select command-centre-input">
                  <option v-for="category in course_categories" :key="category" :value="category">{{ category }}</option>
                </select>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="command-centre-field">
                <label class="command-centre-label">Audience (role)</label>
                <select v-model="course_form.audience" class="form-select command-centre-input">
                  <option v-for="audience in course_audiences" :key="audience" :value="audience">{{ audience }}</option>
                </select>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="command-centre-field">
                <label class="command-centre-label">Linked policy</label>
                <input
                  v-model="course_form.linked_policy"
                  type="text"
                  class="form-control command-centre-input"
                  placeholder="e.g. Data protection policy"
                />
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="command-centre-field">
                <label class="command-centre-label">Linked control</label>
                <input
                  v-model="course_form.linked_control"
                  type="text"
                  class="form-control command-centre-input"
                  placeholder="e.g. C-216 (optional)"
                />
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="command-centre-field">
                <label class="command-centre-label">Assigned (people)</label>
                <input
                  v-model="course_form.assigned_people"
                  type="number"
                  min="0"
                  class="form-control command-centre-input"
                />
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="command-centre-field">
                <label class="command-centre-label">Completed (people)</label>
                <input
                  v-model="course_form.completed_people"
                  type="number"
                  min="0"
                  class="form-control command-centre-input"
                />
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="command-centre-field">
                <label class="command-centre-label">Pass rate (%)</label>
                <input
                  v-model="course_form.pass_rate"
                  type="number"
                  min="0"
                  max="100"
                  class="form-control command-centre-input"
                />
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="command-centre-field">
                <label class="command-centre-label">Validity</label>
                <input
                  v-model="course_form.validity"
                  type="text"
                  class="form-control command-centre-input"
                  placeholder="e.g. 12 months"
                />
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="command-centre-field">
                <label class="command-centre-label">Cohort expiry</label>
                <input
                  v-model="course_form.cohort_expiry"
                  type="text"
                  class="form-control command-centre-input"
                  placeholder="e.g. 12 Jun 2026"
                />
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="command-centre-field">
                <label class="command-centre-label">Status</label>
                <select v-model="course_form.status" class="form-select command-centre-input">
                  <option v-for="status in course_statuses" :key="status" :value="status">{{ status }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="finance-modal-footer employees-modal-footer">
          <button type="button" class="btn btn-link text-muted text-decoration-none" @click="close_course_modal">Cancel</button>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="create_course">
            <i class="bi bi-plus-lg"></i>
            <span>Create</span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="active_course_drawer"
      class="command-centre-drawer-backdrop"
      @click.self="close_course_drawer"
    >
      <aside class="command-centre-drawer training-course-drawer" @click.stop>
        <div class="command-centre-drawer-header training-course-drawer-header">
          <div class="training-course-drawer-heading">
            <div class="command-centre-drawer-kicker">{{ active_course_drawer.section }}</div>
            <h2 class="command-centre-drawer-title">{{ active_course_drawer.title }}</h2>
            <p class="command-centre-drawer-subtitle">{{ active_course_drawer.subtitle }}</p>
            <div class="training-course-drawer-status-row">
              <span class="badge rounded-pill training-course-status-pill" :class="active_course_drawer.status_class">{{ active_course_drawer.status }}</span>
            </div>
          </div>
          <div class="training-course-drawer-actions">
            <button type="button" class="command-centre-drawer-close training-course-drawer-close" @click="close_course_drawer">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <div class="command-centre-drawer-body training-course-drawer-body">
          <div class="training-course-stats-grid">
            <article class="training-course-stat-card">
              <div class="command-centre-drawer-kicker">Completion</div>
              <div class="training-course-stat-row">
                <div class="complyon-stat-value text-l">{{ active_course_drawer.completion }}%</div>
                <div class="employees-progress-value">{{ active_course_drawer.completion }}%</div>
              </div>
              <div class="training-course-progress">
                <div class="progress rounded-pill">
                  <div
                    class="progress-bar rounded-pill"
                    :class="active_course_drawer.completion_tone === 'success' ? 'bg-success' : active_course_drawer.completion_tone === 'warning' ? 'bg-warning' : 'bg-danger'"
                    :style="{ width: active_course_drawer.completion + '%' }"
                  ></div>
                </div>
              </div>
            </article>

            <article class="training-course-stat-card">
              <div class="command-centre-drawer-kicker">Pass rate</div>
              <div class="complyon-stat-value text-l">{{ active_course_drawer.pass_rate }}%</div>
              <div class="training-course-badge-row">
                <span class="badge rounded-pill training-course-inline-pill" :class="active_course_drawer.pass_tone === 'success' ? 'bg-success-subtle text-success-emphasis' : active_course_drawer.pass_tone === 'warning' ? 'bg-warning-subtle text-warning-emphasis' : 'bg-danger-subtle text-danger-emphasis'">
                  {{ active_course_drawer.pass_note }}
                </span>
              </div>
            </article>

            <article class="training-course-stat-card">
              <div class="command-centre-drawer-kicker">Outstanding</div>
              <div class="complyon-stat-value text-l">{{ active_course_drawer.outstanding }}</div>
              <div class="training-course-stat-note">of 210</div>
            </article>

            <article class="training-course-stat-card">
              <div class="command-centre-drawer-kicker">Cohort expiry</div>
              <div class="complyon-stat-value text-s">{{ active_course_drawer.cohort_expiry }}</div>
              <div class="training-course-badge-row">
                <span class="badge rounded-pill training-course-inline-pill" :class="active_course_drawer.cohort_state_class">{{ active_course_drawer.cohort_state }}</span>
              </div>
            </article>
          </div>

          <section class="training-course-section">
            <div class="training-course-section-heading">Linked to</div>
            <div class="training-course-links-grid">
              <article
                v-for="item in active_course_drawer.linked_to"
                :key="active_course_drawer.title + '-' + item.label"
                class="training-course-link-card"
              >
                <i :class="[item.icon, 'training-course-link-icon']"></i>
                <div class="min-w-0">
                  <div class="training-course-link-label">{{ item.label }}</div>
                  <div class="training-course-link-value">{{ item.value }}</div>
                </div>
              </article>
            </div>
          </section>

          <section v-if="active_course_drawer.triggered_items && active_course_drawer.triggered_items.length" class="training-course-section">
            <div class="d-flex align-items-center justify-content-between gap-3">
              <div class="training-course-section-heading">Triggered by incidents & findings</div>
              <div class="training-course-section-count">{{ active_course_drawer.triggered_items.length }}</div>
            </div>
            <div class="training-course-list">
              <article
                v-for="item in active_course_drawer.triggered_items"
                :key="active_course_drawer.title + '-' + item.title"
                class="training-course-list-item"
              >
                <div class="d-flex align-items-start gap-2">
                  <i class="bi bi-arrow-repeat training-course-list-icon"></i>
                  <div class="min-w-0">
                    <div class="training-course-list-title">{{ item.title }}</div>
                    <div class="training-course-list-note">{{ item.subtitle }}</div>
                  </div>
                </div>
                <span class="badge rounded-pill training-course-inline-pill" :class="item.status_class">{{ item.status }}</span>
              </article>
            </div>
          </section>

          <section class="training-course-section">
            <div class="d-flex align-items-center justify-content-between gap-3">
              <div class="training-course-section-heading">Assessment results & certificates</div>
              <div class="training-course-section-count">{{ active_course_drawer.results ? active_course_drawer.results.length : 0 }}</div>
            </div>
            <div v-if="active_course_drawer.results && active_course_drawer.results.length" class="training-course-list">
              <article
                v-for="item in active_course_drawer.results"
                :key="active_course_drawer.title + '-' + item.title"
                class="training-course-list-item"
              >
                <div class="d-flex align-items-start gap-2">
                  <i :class="[item.icon, 'training-course-list-icon']"></i>
                  <div class="min-w-0">
                    <div class="training-course-list-title">{{ item.title }}</div>
                    <div class="training-course-list-note">{{ item.subtitle }}</div>
                  </div>
                </div>
                <span class="badge rounded-pill training-course-inline-pill" :class="item.status_class">{{ item.status }}</span>
              </article>
            </div>
            <div v-else class="training-course-empty-state">No individual records captured.</div>
          </section>
        </div>

        <div class="command-centre-drawer-footer training-course-drawer-footer">
          <button type="button" class="btn btn-outline-secondary training-course-secondary-btn">Send reminders</button>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn training-course-primary-btn">Edit course</button>
        </div>
      </aside>
    </div>
  </div>
</div>
